import { execAdmin } from '@src/apis/reware/utils/exec';

import type {
  FetchInventoryItemsVariables,
  InventoryConstants,
  InventoryItemsPage,
} from './types';

// Cloudinary resize args for thumbnails (same convention as reware-admin)
const THUMBNAIL = { height: 300, width: 300, quality: 80, crop: 'fit' };

interface FetchInventoryItemsResponse {
  inventoryItems: InventoryItemsPage;
}

/**
 * Partner-scoped version of the admin `inventoryItems` query used by the
 * reware-admin Inventory Items portal. The row selection mirrors the admin
 * portal's so the expanded item view has full field parity.
 */
export const fetchInventoryItems = async (
  variables: FetchInventoryItemsVariables
): Promise<InventoryItemsPage> => {
  const query = `
    query fetchInventoryItems(
      $partnerId: Int
      $page: Int
      $pageSize: Int
      $sort: String
      $state: String
      $condition: String
      $sku: String
      $parentSku: String
      $brand: String
      $itemId: Int
      $searchQuery: String
      $height: Int
      $width: Int
      $quality: Int
      $crop: String
    ) {
      inventoryItems(
        partnerId: $partnerId
        page: $page
        pageSize: $pageSize
        sort: $sort
        state: $state
        condition: $condition
        sku: $sku
        parentSku: $parentSku
        brand: $brand
        itemId: $itemId
        searchQuery: $searchQuery
      ) {
        count
        page
        pageSize
        rows {
          id
          uuid
          state
          condition
          deployedCondition
          recommendedCondition
          custodyStatus
          isUnsellable
          price
          notes(omitMeasurements: ALWAYS)
          storefrontPhrase
          imageUrls
          thumbnailImageUrls: imageUrls(height: $height, width: $width, quality: $quality, crop: $crop)
          catalogImageUrls
          allCatalogImageUrls
          inventoryImageUrls
          workstationImageUrls
          sendbackImageUrls
          sendbackThumbnailImageUrls: sendbackImageUrls(height: $height, width: $width, quality: $quality, crop: $crop)
          isImageFromInventory
          authStatus
          authMethod
          photoInheritanceConditionCodes
          photoUploadPositions {
            position
            required
          }
          canBeOrdered
          canUseCatalogPhotography
          isKeyless
          isFlagged
          isSupplierSendback
          needsCleaning
          needsPhotography
          needsRepair
          newWithTags
          rejectedReason
          partnerTier
          shipmentId
          sourcePartnerId
          preprintedLpn
          troveSerialNumber
          resaleUpc {
            upc
            parentSku
          }
          measurements
          wmsStatus {
            status
            code
          }
          location {
            id
            code
            parentLocationCode
            kind
            warehouse {
              code
              purpose
            }
          }
          catalogItem {
            id
            title
            description
            brand
            sku
            parent_sku
            partnerSku
            size
            color
            display_color
            availability
            availabilityCheckin
            mitiAvailability
            upc
            rubricCode
            seasonCode
            season_display
            prop65_code
            prop65_message
            shippingSurchargeAmount
            price
            originalPrice
            usedPrice
            lastKnownRetailPrice
            lastKnownOutletPrice
            msrpPrice
            yerdlePrice
            yadjustedPrice
            yadjustedCode
            fabric {
              id
              code
              name
              content
            }
            catalogFacets {
              tag
              name
            }
            measurements {
              fields {
                field
                displayName
                units
              }
            }
          }
          stateTransitions {
            id
            createdAt
            changeType
            changeFrom
            changeTo
            reason
            note
            changeDetails
            user {
              name
            }
          }
          repairStatus
          repairJobs {
            id
            uuid
            status
            createdAt
            startedAt
            declinedAt
            completedAt
            repairJobDetails {
              id
              uuid
              type
              location
            }
          }
          cleaningStatus
          cleaningJobs {
            id
            uuid
            status
            createdAt
            startedAt
            declinedAt
            completedAt
          }
          photographyStatus
          photographyJobs {
            id
            uuid
            status
            createdAt
            startedAt
            declinedAt
            completedAt
          }
          escalations {
            id
            triggerTypeDisplay
            shipmentTypeDisplay
            reasonDisplay
            closedReason
            closedAt
            createdAt
          }
          order {
            id
            orderPlacedAt
          }
          shipment {
            id
            source
          }
          tradeinItems {
            id
            createdAt
            tradein {
              id
              tradeinType
            }
          }
          inventoryItemExternals {
            externalIdentifier
            externalSystem
            externalUrl
          }
          productVariant {
            productId
            subdomain
            handle
          }
        }
      }
    }
  `;

  const data = await execAdmin<
    FetchInventoryItemsVariables & typeof THUMBNAIL,
    FetchInventoryItemsResponse
  >(query, { variables: { ...variables, ...THUMBNAIL } });

  return data.inventoryItems;
};

interface FetchInventoryConstantsResponse {
  application: {
    constants: {
      inventoryItem: {
        states: InventoryConstants['states'];
        allConditions: InventoryConstants['conditions'];
        rejectReasons: InventoryConstants['rejectReasons'];
      };
    };
  };
}

/** Item states, condition grades, and reject reasons for filters and dialogs. */
export const fetchInventoryConstants = async (): Promise<InventoryConstants> => {
  const query = `
    query fetchInventoryConstants {
      application {
        constants {
          inventoryItem {
            states {
              code
              displayName
            }
            allConditions {
              code
              displayName
            }
            rejectReasons {
              code
              displayName
            }
          }
        }
      }
    }
  `;

  const data = await execAdmin<Record<string, never>, FetchInventoryConstantsResponse>(
    query,
    { variables: {} }
  );

  const { states, allConditions, rejectReasons } = data.application.constants.inventoryItem;
  return { states, conditions: allConditions, rejectReasons };
};
